import { Component, DestroyRef, OnInit } from '@angular/core';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UsuarioService } from '../../../shared/services/apis/usuario/usuario.service';
import { PerfilService } from '../../../shared/services/apis/usuario/perfil.service';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { UsuarioLookupResponse } from '../../../shared/services/responses/usuario/usuario-lookup.response';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { UsuarioRequest } from '../../../shared/services/requests/usuario/usuario.request';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { UsuarioResponse } from '../../../shared/services/responses/usuario/usuario.response';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UsuarioVerificarEmailService } from '../../../shared/services/apis/usuario/usuario-verificar-email.service';
import { UsuarioVerificarEmailResponse } from '../../../shared/services/responses/usuario/usuario-verificar-email.response';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Masks, MaskService } from '../../../shared/services/mask.service';
import { cpfValidator } from '../../../shared/validators/cpf.validator';
import { requiredTrim } from '../../../shared/validators/string-trim.validator';
import { emailValidator } from '../../../shared/validators/email.validator';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { PerfilRestricaoStruct } from '../../../shared/services/structs/usuario/perfil-restricao.struct';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { EMPTY, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { PerfilRestricaoResponse } from '../../../shared/services/responses/usuario/perfil-restricao.response';

@Component({
  selector: 'app-usuario-cadastro',
  imports: [
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSelectModule,
    MatTooltipModule,
    MatSlideToggleModule,
    NgxMaskDirective,
    FooterActionComponent,
    AlertCardComponent,
    HeaderComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './usuario-cadastro.component.html',
  styleUrl: './usuario-cadastro.component.css',
})
export class UsuarioCadastroComponent implements OnInit {
  public moduloEnum: ModuloEnum;
  public funcionalidadeEnum: FuncionalidadeEnum;

  constructor(
    private formBuilder: FormBuilder,
    private usuarioService: UsuarioService,
    private perfilService: PerfilService,
    private alertService: AlertService,
    private destroyRef: DestroyRef,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private usuarioVerificarEmailService: UsuarioVerificarEmailService,
    private maskService: MaskService
  ) {
    this.moduloEnum = ModuloEnum.Configuracoes;
    this.funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Usuario;
  }

  private perfisSelecionadosSubscription?: Subscription;

  public model: FormGroup;
  public _isLoading: boolean = false;
  public _isLoadingVerificarEmail: boolean = false;
  public _usuarioLookupResponse: UsuarioLookupResponse;
  public _usuarioResponse: UsuarioResponse;
  public idUsuario: number | null = null;
  public btnSalvarDisable: boolean = true;
  public nomeEmpresaLogada: string;
  public masks: Masks;
  public listaRestricaoPerfil: PerfilRestricaoStruct[] = [];

  readonly diasSemana: string[] = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  ngOnInit(): void {
    this.masks = this.maskService.getMasks();

    if (this.activatedRoute.snapshot.paramMap.get('idUsuario')) {
      this.idUsuario = parseInt(this.activatedRoute.snapshot.paramMap.get('idUsuario') ?? '');
    }

    this.model = this.formBuilder.group({
      email: new FormControl({ value: '', disabled: false }, [Validators.required, emailValidator()]),
      nomeUsuario: new FormControl({ value: '', disabled: true }, [Validators.required, requiredTrim]),
      telefone: new FormControl({ value: '', disabled: true }),
      cpf: new FormControl({ value: '', disabled: true }, [Validators.required, cpfValidator()]),
      listaIdPerfil: new FormControl({ value: '', disabled: false }),
      master: new FormControl({ value: '', disabled: false }),
    });

    this.perfisSelecionadosSubscription = this.model
      .get('listaIdPerfil')
      ?.valueChanges.pipe(
        map(() => this.getPerfisSelecionadosIds()),
        debounceTime(0),
        distinctUntilChanged((a, b) => a.join('|') === b.join('|')),
        tap(ids => {
          if (ids.length === 0) {
            this.listaRestricaoPerfil = [];
          }
        }),
        switchMap(ids => {
          if (ids.length === 0) {
            return EMPTY;
          }

          return this.perfilService.ObterRestricoesPorIds(ids);
        })
      )
      .subscribe({
        next: (response: PerfilRestricaoResponse) => {
          if (!response) {
            return;
          }

          if (response.isError) {
            this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
            return;
          }

          this.listaRestricaoPerfil = (response.listaRestricaoPerfil ?? []).map(restricao => ({
            ...restricao,
            nomePerfil: this.getNomePerfil(restricao.idPerfil) || restricao.nomePerfil || `Perfil ${restricao.idPerfil}`,
            listaIpRestrito: restricao.listaIpRestrito ?? [],
            listaAcessoHorario: restricao.listaAcessoHorario ?? [],
          }));
        },
        error: error => {
          this.alertService.show('Erro inesperado', error, AlertType.error);
        },
      });

    this.destroyRef.onDestroy(() => this.perfisSelecionadosSubscription?.unsubscribe());

    this.popularLookup();

    if (this.idUsuario) {
      this.popularDadosUsuario();
    }
  }

  popularLookup(): void {
    this.usuarioService.lookup().subscribe({
      next: (usuarioLookupResponse: UsuarioLookupResponse) => {
        if (usuarioLookupResponse.isError) {
          this.alertService.show('Aviso', usuarioLookupResponse.errorDescription, AlertType.warning);
          return;
        }

        this._usuarioLookupResponse = usuarioLookupResponse;
        this.aplicarNomesLookupNasRestricoes();
      },
      error: error => {
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  popularDadosUsuario(): void {
    this.usuarioService.ObterPorId(this.idUsuario ?? 0).subscribe({
      next: (usuarioResponse: UsuarioResponse) => {
        if (usuarioResponse.isError) {
          this.alertService.show('Aviso', usuarioResponse.errorDescription, AlertType.warning);
          return;
        }

        this.model.get('nomeUsuario')?.setValue(usuarioResponse.usuario.nomeUsuario, { emitEvent: false });
        this.model.get('email')?.setValue(usuarioResponse.usuario.email, { emitEvent: false });
        this.model.get('telefone')?.setValue(usuarioResponse.usuario.telefone, { emitEvent: false });
        this.model.get('cpf')?.setValue(usuarioResponse.usuario.cpf, { emitEvent: false });
        this.model.get('listaIdPerfil')?.setValue(usuarioResponse.listaIdPerfil, { emitEvent: false });
        this.model.get('master')?.setValue(usuarioResponse.masterEmpresa, { emitEvent: false });

        this._usuarioResponse = usuarioResponse;
        this.model.get('listaIdPerfil')?.updateValueAndValidity({ emitEvent: true });

        this.model.get('email')?.disable();
        this.model.get('listaIdPerfil')?.enable();
        this.model.get('master')?.enable();
        this.btnSalvarDisable = false;

        if (usuarioResponse.emrpesaCriadora) {
          this.model.get('nomeUsuario')?.enable();
          this.model.get('telefone')?.enable();
          this.model.get('cpf')?.enable();
        }
      },
      error: error => {
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  private normalizeListaIdPerfil(value: unknown): number[] {
    if (value == null) {
      return [];
    }

    if (Array.isArray(value)) {
      const parsed = value.map(v => Number(v)).filter(v => Number.isFinite(v) && v > 0);
      return Array.from(new Set(parsed)).sort((a, b) => a - b);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }

      const parsed = trimmed
        .split(',')
        .map(part => Number(part.trim()))
        .filter(v => Number.isFinite(v) && v > 0);
      return Array.from(new Set(parsed)).sort((a, b) => a - b);
    }

    const asNumber = Number(value);
    const single = Number.isFinite(asNumber) && asNumber > 0 ? [asNumber] : [];
    return Array.from(new Set(single)).sort((a, b) => a - b);
  }

  private getPerfisSelecionadosIds(): number[] {
    const rawIds = this.model.get('listaIdPerfil')?.value as unknown;
    return this.normalizeListaIdPerfil(rawIds);
  }

  private getNomePerfil(idPerfil: number): string {
    const perfil = this._usuarioLookupResponse?.listaPerfil?.find(p => p.idPerfil === idPerfil);
    return perfil?.nome ?? '';
  }

  private aplicarNomesLookupNasRestricoes(): void {
    if (!this.listaRestricaoPerfil?.length) {
      return;
    }

    this.listaRestricaoPerfil = this.listaRestricaoPerfil.map(r => {
      const nomeLookup = this.getNomePerfil(r.idPerfil);
      return nomeLookup ? { ...r, nomePerfil: nomeLookup } : r;
    });
  }

  public temPerfisSelecionados(): boolean {
    return this.getPerfisSelecionadosIds().length > 0;
  }

  verificaEmail(): void {
    if (this._isLoadingVerificarEmail) return;

    const email: string = this.model.get('email')?.value;

    if (!email || this.model.get('email')?.invalid) {
      this.alertService.show('Aviso', 'informe um email válido', AlertType.warning);
      return;
    }
    this._isLoadingVerificarEmail = true;
    this.usuarioVerificarEmailService.get(email).subscribe({
      next: (usuarioVerificarEmailResponse: UsuarioVerificarEmailResponse) => {
        if (usuarioVerificarEmailResponse.isError) {
          this._isLoadingVerificarEmail = false;
          this.alertService.show('Aviso', usuarioVerificarEmailResponse.errorDescription, AlertType.warning);
          return;
        }
        this._isLoadingVerificarEmail = false;
        if (usuarioVerificarEmailResponse.usuarioNaoExiste) {
          this.alertService.show(
            'Informação',
            'Email livre para ser cadastrado na sua empresa',
            AlertType.info
          );
          this.model.get('email')?.setValue(email.trim());
          this.enableFields();
          return;
        }

        if (usuarioVerificarEmailResponse.podeVinularEmpresa) {
          this.abrirModalVincularUsuario(email);
        }
      },
      error: error => {
        this._isLoadingVerificarEmail = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  abrirModalVincularUsuario(email: string): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Vincular usuário deste email a sua empresa',
        innerHtml: `<p>O usuário <strong>${email}</strong> já possui uma conta em outra empresa parceira. Deseja vinculá-lo também à sua organização?</p> 
        <p>Nota: Se você confirmar, enviaremos um e-mail notificando o usuário sobre o novo vínculo.</p>`,
        buttonConfirm: 'Vincular à minha empresa',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result && result.confirm) {
          this.vincularUsuario(email);
        }
      },
    });
  }

  vincularUsuario(email: string): void {
    this.usuarioVerificarEmailService.post(email).subscribe({
      next: (returnStruct: ReturnStruct) => {
        if (returnStruct.isError) {
          this.alertService.show('Aviso', returnStruct.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Usuário vinculado a sua empresa.', AlertType.success);
        this.router.navigate(['/configuracoes/usuario']);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  submit(): void {
    if (this._isLoading || this.model.invalid) {
      return;
    }

    const usuarioRequest: UsuarioRequest = new UsuarioRequest();

    usuarioRequest.idUsuario = this.idUsuario;
    usuarioRequest.email = this.model.get('email')?.value.trim();
    usuarioRequest.nomeUsuario = this.model.get('nomeUsuario')?.value.trim();
    usuarioRequest.telefone = this.model.get('telefone')?.value;
    usuarioRequest.cpf = this.model.get('cpf')?.value;
    usuarioRequest.master = !!this.model.get('master')?.value;
    usuarioRequest.listaIdPerfil = this.model.get('listaIdPerfil')?.value
      ? this.model.get('listaIdPerfil')?.value
      : [];

    if (!this.idUsuario) {
      this.novo(usuarioRequest);
    } else {
      this.alterar(usuarioRequest);
    }
  }

  private novo(usuarioRequest: UsuarioRequest): void {
    this._isLoading = true;
    this.usuarioService.Incluir(usuarioRequest).subscribe({
      next: (returnStruct: ReturnStruct) => {
        this._isLoading = false;
        if (returnStruct.isError) {
          this.alertService.show('Aviso', returnStruct.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Usuário incluido com sucesso.', AlertType.success);
        this.router.navigate(['/configuracoes/usuario']);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  private alterar(usuarioRequest: UsuarioRequest): void {
    this._isLoading = true;
    this.usuarioService.Alterar(this.idUsuario ?? 0, usuarioRequest).subscribe({
      next: (returnStruct: ReturnStruct) => {
        this._isLoading = false;
        if (returnStruct.isError) {
          this.alertService.show('Aviso', returnStruct.errorDescription, AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Usuário incluido com sucesso.', AlertType.success);
        this.router.navigate(['/configuracoes/usuario']);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  abrirModalAlterarSenhaUsuario(): void {
    if (!this.idUsuario) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: `Alterar senha do usuário`,
        description: `Ao alterar a senha do usuário ele irá receber, no email cadastrado, sua senha temporária. Ao fazer login ele obrigatoriamente precisa inserir a senha definitiva.`,
        buttonConfirm: 'Alterar a senha',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result && result.confirm) {
          this.alterarSenha();
        }
      },
    });
  }

  cancelar(): void {
    this.router.navigate(['/configuracoes/usuario']);
  }

  private alterarSenha(): void {
    this.usuarioService.refefinirSehha(this.idUsuario).subscribe({
      next: (returnStruct: ReturnStruct) => {
        this._isLoading = false;
        if (returnStruct.isError) {
          this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
          return;
        }

        this.alertService.show('Sucesso', 'Senha redefinida com sucesso.', AlertType.success);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error, AlertType.error);
      },
    });
  }

  public disableFields(): void {
    this.model.get('nomeUsuario')?.disable();
    this.model.get('telefone')?.disable();
    this.model.get('cpf')?.disable();
    this.model.get('listaIdPerfil')?.disable();
    this.model.get('master')?.disable();
    this.btnSalvarDisable = true;
  }

  public enableFields(): void {
    this.model.get('nomeUsuario')?.enable();
    this.model.get('telefone')?.enable();
    this.model.get('cpf')?.enable();
    this.model.get('listaIdPerfil')?.enable();
    this.model.get('master')?.enable();
    this.btnSalvarDisable = false;
  }
}
